import React, { useContext, memo } from 'react';
import styled from 'styled-components';

import { fieldHelper } from '@builttocreate/engine-utils';

import FieldTypes from '../../../constants/FieldTypes';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldModes from '../../../constants/FieldModes';
import FileViews from '../../../constants/FileViews';

import { validateField } from '../../../lib/validation';

import TextField from './TextField';
import DateField from './DateField';
import BlockField from './BlockField';
import TextareaField from './TextareaField';
import ImageField from './ImageField';
import FileField from './FileField';
import RTEField from './RTEField';
import RichTextField from './rte/RichTextField';
import DropdownField from './DropdownGenericField';
import DropdownSelectionField from './DropdownSelectionField';
import SignatureField from './SignatureField';
import MultiSelectField from './MultiSelectField';
import MultiSelectSelectionField from './MultiSelectSelectionField';
import ChartField from './chart/ChartField';
import CollectionField from './collection/CollectionField';
import PricingTableField from './pricing/PricingTableField';

import TableFieldv2 from './table/TableFieldv2';
import TableTextField from './table/TableTextField';
import TableNumberField from './table/TableNumberField';
import TableImageField from './table/TableImageField';
import TableDropdownField from './table/TableDropdownField';
import TableDropdownSelectionField from './table/TableDropdownSelectionField';
import TableMultiselectField from './table/TableMultiselectField';
import TableDateField from './table/TableDateField';
import TableBlockField from './table/TableBlockField';
import TableBarcodeField from './table/TableBarcodeField';
import TableSignatureField from './table/TableSignatureField';

import Icon from '../../misc/Icon';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';
import { getFieldThemeObject } from '../../../theme/getThemeObject';

const { isFieldEmpty } = fieldHelper;

/**
 * <-----------------------NOTICE------------------>
 * Overflow is visible in readOnly mode to ensure that the field value
 * appears fully without being cut off due to inadequate field height
 */
const StyledFieldWrapper = styled.div`
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  overflow: ${(props) => (props.disabled ? 'visible' : ' hidden')};
`;

const StyledField = styled.div`
  height: 100%;
`;

const StyledFieldIndicatorWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: -6px;
  left: -4px;
`;

const StyledFieldIndicator = styled.div`
  width: 15px;
  height: 15px;
  text-align: center;
  border-radius: 100px;
  border-color: transparent;
  z-index:1;
  margin-right: 2px;
  cursor: default;
  svg {
    font-size: 14px;
    vertical-align: 0;
  }
`;

const Field = (props) => {

  const {
    _id,
    field,
    identifier,
    config,
    type,
    view,
    displayType,
    columnType,
    disabled,
    required,
    hidden,
    options,
    cellValue,

    fieldMode,
    fieldPresentationMode,
    fieldDefaultStyles,

    /**
     * Properties used for adjusting fields
     * for PDF Export and Page Breaks. Do not
     * remove.
     */
    layouts,
    licenseValidation,

  } = props;

  /**
   * Step 1: Theming and Field Styles
   */
  const theme = useContext(ThemeContext);
  const fieldTheme = getFieldThemeObject({
    mode: fieldMode,
    fieldPresentationMode,
    fieldPosition: props,
    fileStyles: fieldDefaultStyles,
    theme: theme?.field,
    sdkTheme: theme
  });

  /**
   * Step 2: Determine file picker configuration. This is primarily used by fields that support file uploads. ie, image, file, table, etc.
   */
  const enableSystemFilePicker = typeof config?.systemFilePicker === 'boolean' ? config?.systemFilePicker : true;

  /**
   * Step 3: Build field props object
   */
  const fieldProps = {
    id: _id,
    mode: fieldMode,
    fieldId: field,
    fieldIdentifier: identifier,
    fieldPositionId: _id,
    isMobileView: view === FileViews.mobile,
    enableSystemFilePicker,
    ...props,
    ...fieldTheme,
  };

  const isValid = () => {
    const { type, hidden, required, value, schema, displayType, fieldPositionSchema, mode } = fieldProps;

    const activeField = {
      type,
      hidden,
      required,
      value,
      schema,
      displayType,
      fieldPositionSchema,
      mode,
    };

    const { status } = validateField(activeField);

    return status !== 'valid';
  };

  let fieldNode = null;
  let displayRequiredFieldIndicators = false;

  if (type === FieldTypes.file) {

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField>
        <FileField {...fieldProps} />
      </StyledField>
    );
  }

  if (type === FieldTypes.image) {

    displayRequiredFieldIndicators = (required && isValid());
    fieldNode = (
      <StyledField>
        <ImageField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.richText) {
    fieldNode = (
      <StyledField>
        <RTEField {...fieldProps} disabled={fieldMode !== FieldModes.edit} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.date) {

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField disabled={disabled}>
        <DateField {...fieldProps} />
      </StyledField>
    );

  }
  else if (
    type === FieldTypes.text
    || type === FieldTypes.number
    || type === FieldTypes.uniqueId
  ) {

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField disabled={disabled}>
        <TextField {...fieldProps} />
      </StyledField>
    );

  }
  else if (type === FieldTypes.rte) {
    displayRequiredFieldIndicators = (required && isValid());
    fieldNode = (
      <StyledField disabled={disabled}>
        <RichTextField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.block) {
    fieldNode = (
      <StyledField>
        <BlockField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.textarea) {

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField disabled={disabled}>
        <TextareaField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.chart) {

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField>
        <ChartField
          {...fieldProps}
        />
      </StyledField>
    );
  }
  else if (type === FieldTypes.signature) {

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField>
        <SignatureField {...fieldProps} disabled={fieldProps.disabled || fieldMode === FieldModes.edit} />
      </StyledField>
    );
  }

  /**
   * Dropdown Fields
   */
  else if (
    type === FieldTypes.dropdown
    && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.text)
  ) {

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField disabled={disabled}>
        <DropdownField {...fieldProps} />
      </StyledField>
    );
  }
  else if (
    type === FieldTypes.dropdown
    && (displayType !== FieldDisplayTypes.original && displayType !== FieldDisplayTypes.text)
  ) {

    if (fieldMode === FieldModes.edit) fieldProps.value = fieldProps.targetValue;

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField>
        <DropdownSelectionField
          {...fieldProps}
          disabled={disabled || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }

  /**
   * Multi-Select Fields
   */
  else if (type === FieldTypes.multiSelect
    && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.horizontal)) {

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField>
        <MultiSelectField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.multiSelect && displayType !== FieldDisplayTypes.original) {

    if (fieldMode === FieldModes.edit) fieldProps.value = fieldProps.targetValue;

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField>
        <MultiSelectSelectionField
          {...fieldProps}
          disabled={disabled || fieldMode === FieldModes.edit}
          value={fieldProps.value || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }

  /**
   * Pricing Table Field
   */
  else if (type === FieldTypes.pricingTable) {
    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField>
        <PricingTableField {...fieldProps} />
      </StyledField>
    );
  }

  /**
   * Collection Field
   */
  else if (type === FieldTypes.collection) {

    displayRequiredFieldIndicators = (required && isValid());

    if (!licenseValidation
      || !licenseValidation.isValid
      || (licenseValidation && !licenseValidation.decodedData.collectionField)
    ) {
      return null;
    }

    fieldNode = (
      <StyledField>
        <CollectionField
          {...fieldProps}
          layouts={layouts}
          disabled={disabled}
        />
      </StyledField>
    );

  }

  /**
   * Table Fields
   */
  else if (type === FieldTypes.table && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.inputGroup)) {

    displayRequiredFieldIndicators = (required && isValid());

    fieldNode = (
      <StyledField>
        <TableFieldv2
          {...fieldProps}
          layouts={layouts}
          disabled={disabled}
        />

      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.text) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={disabled}>
        <TableTextField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.barcode) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={disabled}>
        <TableBarcodeField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.signature) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={disabled}>
        <TableSignatureField
          {...fieldProps}
          disabled={disabled || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.number) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={disabled}>
        <TableNumberField {...fieldProps} />
      </StyledField>
    );

  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.image) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField>
        <TableImageField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.dropdown) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField disabled={disabled}>
        <TableDropdownField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.multiSelect) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField disabled={disabled}>
        <TableMultiselectField {...fieldProps} />
      </StyledField>
    );
  }
  else if (
    type === FieldTypes.table
    && columnType === FieldTableColumnTypes.dropdown
    && displayType !== FieldDisplayTypes.original
    && displayType !== FieldDisplayTypes.text
  ) {

    if (fieldMode === FieldModes.edit) fieldProps.cellValue = fieldProps.targetValue;

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField>
        <TableDropdownSelectionField
          {...fieldProps}
          disabled={disabled || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.date) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={disabled}>
        <TableDateField {...fieldProps} />
      </StyledField>
    );

  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.block) {

    displayRequiredFieldIndicators = (required && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField disabled={disabled}>
        <TableBlockField {...fieldProps} />
      </StyledField>
    );
  }

  const indicatorNodes = (
    <StyledFieldIndicatorWrapper>
      {!displayRequiredFieldIndicators || fieldMode === FieldModes.readonly ? null : (
        <StyledFieldIndicator>
          <Icon icon={ThemeIcons.settings.required} color={theme.dangerColor} />
        </StyledFieldIndicator>
      )}
      {!hidden || fieldMode !== FieldModes.edit ? null : (
        <StyledFieldIndicator>
          <Icon icon={ThemeIcons.settings.hide} color={theme.warningColor} />
        </StyledFieldIndicator>
      )}
    </StyledFieldIndicatorWrapper>
  );

  return (
    <StyledFieldWrapper {...{ disabled }}>
      {indicatorNodes}
      {fieldNode}
    </StyledFieldWrapper>
  );

};

export default memo(Field);
